// Hooks de datos de Control MDM contra el bloque backoffice de `loan-credit-business`.
// Aquí no se arma ninguna URL ni se toca `fetch`: eso vive en `api/services/loan-credit/`.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type InfiniteData,
    type QueryClient,
} from '@tanstack/react-query';
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import { mdmBackofficeApi } from '@/api/services/loan-credit';
import type {
    LoanDeviceFleetItem,
    LoanDeviceFleetResponse,
    LoanDeviceNotifyRequest,
    LoanDeviceReleaseRequest,
    LoanDeviceUnlockReason,
} from '@/api/services/loan-credit/contract';
import type { MdmActionKey } from '../mdmActions';
import type { MdmAuditEntry, MdmFleetDevice, MdmRefreshReview } from '../types';
import { mdmActionErrorMessage } from '../actionErrors';
import { announceMdmFailure, announceMdmOutcome, type MdmOutcomeContext } from '../mdmOutcome';
import { readActionItemOutcome, toAuditEntry, toFleetDevice, toRefreshReview } from './mappers';
import { INSTALLMENTS_LIST_KEY } from '../../installments/api/queryKeys';
import { CREDIT_FILE_DEVICE_KEY, MDM_FLEET_KEY, MDM_OPERATIONS_KEY } from './queryKeys';

export interface MdmDeviceOperationsResult {
    entries: MdmAuditEntry[];
    isLoading: boolean;
    /** Una lectura fallida NO es una bitácora vacía: la pestaña tiene que poder distinguirlas. */
    error: Error | null;
}

/**
 * La bitácora de UN equipo, leída por crédito. Con `?credit=` en la URL sale en el primer render,
 * en paralelo con la flota; si no, espera a que se elija el equipo.
 */
export function useMdmDeviceOperations(creditId: string | undefined): MdmDeviceOperationsResult {
    const enabled = creditId !== undefined;
    const operations = useQuery({
        queryKey: [...MDM_OPERATIONS_KEY, creditId],
        enabled,
        queryFn: () => mdmBackofficeApi.listMdmDeviceOperations(creditId ?? ''),
        staleTime: 30_000,
    });

    const entries = useMemo(() => {
        if (creditId === undefined) return [];
        return (operations.data?.items ?? []).map((item) => toAuditEntry(item, creditId));
    }, [creditId, operations.data]);

    return {
        entries,
        // Sin equipo abierto la query queda deshabilitada, y `isLoading` de react-query sigue en
        // `true`: eso pintaría un cargando eterno en la ficha vacía.
        isLoading: enabled && operations.isLoading,
        error: enabled ? operations.error : null,
    };
}

/**
 * Las ÓRDENES que este hook manda. La consulta al proveedor tiene endpoint, pero no está aquí: va
 * por `useMdmDeviceRefresh` porque su resultado se pinta, no se anuncia y se tira.
 */
export const MDM_EXECUTABLE_ACTIONS = [
    'lock',
    'unlock',
    'extendValidity',
    'pinUnlock',
    'notify',
    'release',
] as const;

export type MdmExecutableAction = (typeof MDM_EXECUTABLE_ACTIONS)[number];

export function isExecutableAction(key: MdmActionKey): key is MdmExecutableAction {
    return (MDM_EXECUTABLE_ACTIONS as readonly string[]).includes(key);
}

/**
 * A quién se le manda la orden. Los endpoints del backoffice son por crédito, así que eso es todo
 * lo que hace falta: Cobranza marca cuotas y nunca lee la flota, pero sí conoce su crédito.
 */
export interface MdmActionTarget {
    creditId: string;
}

export interface MdmActionRun {
    key: MdmExecutableAction;
    /**
     * Los equipos que reciben la orden. Control MDM manda siempre uno; Cuotas sí opera en lote
     * sobre los créditos que el operador marcó.
     */
    devices: readonly MdmActionTarget[];
    /** Motivo del bloqueo, sólo para `lock`. Ausente = bloqueo manual de un administrador. */
    lockReason?: LoanDeviceLockReasonEnum;
    /** Motivo del desbloqueo, sólo para `unlock`. Ausente = no se declara. */
    unlockReason?: LoanDeviceUnlockReason;
    /** Nota del operador para `lock`, `unlock` y `extendValidity`; queda en la bitácora con su correo. */
    comment?: string;
    /** Nuevo corte ISO, solo para `extendValidity`. */
    newDueDate?: string;
    /** El código que muestra el equipo, solo para `pinUnlock`. Lo dicta el cliente. */
    devicePasskey?: string;
    /** El aviso en pantalla ya armado, solo para `notify`. */
    notification?: LoanDeviceNotifyRequest;
    /** El motivo y la nota de la liberación, solo para `release`. */
    release?: LoanDeviceReleaseRequest;
    /** Cómo se anuncia el desenlace si la pantalla que la lanzó ya se desmontó. */
    outcomeContext?: MdmOutcomeContext;
}

/** Cuántos equipos del lote respondieron bien y cuántos no. */
export interface MdmActionOutcome {
    /** El proveedor aceptó la orden (`SUCCESS`) o la dejó en vuelo (`PENDING`). */
    succeeded: number;
    failed: number;
    /** De los que aceptaron, cuántos quedaron `PENDING`: el equipo aún no confirma. */
    pending?: number;
    /** Motivo del primer fallo del lote: es el que se le muestra al operador. */
    firstErrorMessage?: string;
    /** Equipos que quedaron desbloqueados y SIN autolock programado. Hay que escalarlos. */
    unprotected: number;
    /**
     * Hora del corte que instaló el bloqueo que se acaba de mandar. Ausente si el proveedor no
     * programó ninguno o si ningún equipo del lote pasó.
     */
    lockEffectiveAt?: string;
    /**
     * Passcode de PIN-UNLOCK. Solo en la acción de un equipo: es de un solo uso y esta pantalla es
     * el único lugar donde se muestra, así que no se persiste en ningún lado.
     */
    pin?: string;
    /** Vigencia del passcode en segundos, tal como la reportó el proveedor. */
    pinValiditySeconds?: number;
}

/** El anuncio de respaldo cuando la pantalla no dijo cómo nombrar la orden. */
function outcomeContextOf(run: MdmActionRun): MdmOutcomeContext {
    return (
        run.outcomeContext ?? {
            key: run.key,
            label: 'Orden MDM',
            scope: `${String(run.devices.length)} ${run.devices.length === 1 ? 'equipo' : 'equipos'}`,
            total: run.devices.length,
        }
    );
}

/** El diálogo no deja confirmar sin estos payloads; si igual faltan, la orden no sale. */
const MISSING_NOTIFICATION_PAYLOAD = 'Notificar llegó sin el aviso armado';
const MISSING_RELEASE_PAYLOAD = 'Liberar del MDM llegó sin el motivo armado';

function runOne(run: MdmActionRun, device: MdmActionTarget): Promise<unknown> {
    // La consulta al proveedor NO sale por aquí: tiene su propio hook porque su resultado se pinta.
    // Este rechazo ataja esa key y cualquier otra que no sea una orden.
    if (!(MDM_EXECUTABLE_ACTIONS as readonly string[]).includes(run.key)) {
        return Promise.reject(new Error(`La acción ${run.key} no se ejecuta como orden MDM`));
    }
    if (run.key === 'lock') {
        // El mensaje de pantalla no se manda: ningún proveedor cableado lo acepta y el lambda lo
        // descarta. Sin motivo declarado el bloqueo es manual, como lo manda Control MDM.
        return mdmBackofficeApi.lockMdmDevice(device.creditId, {
            reason: run.lockReason ?? LoanDeviceLockReasonEnum.ADMIN_MANUAL,
            ...(run.comment ? { comment: run.comment } : {}),
        });
    }
    if (run.key === 'unlock') {
        return mdmBackofficeApi.unlockMdmDevice(device.creditId, {
            ...(run.unlockReason ? { reason: run.unlockReason } : {}),
            ...(run.comment ? { comment: run.comment } : {}),
        });
    }
    if (run.key === 'pinUnlock') {
        // El diálogo no habilita confirmar sin el código completo, así que aquí nunca llega vacío.
        return mdmBackofficeApi.pinUnlockMdmDevice(device.creditId, {
            devicePasskey: run.devicePasskey ?? '',
        });
    }
    // Sin payload la orden falla: mandar otra operación en su lugar pintaría «enviado» sobre un
    // equipo al que nadie notificó ni liberó.
    if (run.key === 'notify') {
        if (!run.notification) return Promise.reject(new Error(MISSING_NOTIFICATION_PAYLOAD));
        return mdmBackofficeApi.notifyMdmDevice(device.creditId, run.notification);
    }
    if (run.key === 'release') {
        if (!run.release) return Promise.reject(new Error(MISSING_RELEASE_PAYLOAD));
        return mdmBackofficeApi.releaseMdmDevice(device.creditId, run.release);
    }
    // El `if` es redundante para el compilador y NO para el runtime: la rama existe para que una
    // key sumada a la lista sin su propio camino no termine extendiendo la vigencia.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (run.key === 'extendValidity') {
        // El diálogo no habilita confirmar sin fecha ni motivo, así que aquí nunca llegan vacíos.
        return mdmBackofficeApi.extendMdmDeviceValidity(device.creditId, {
            newDueDate: run.newDueDate ?? '',
            ...(run.comment ? { comment: run.comment } : {}),
        });
    }
    // Sin salida por descarte: una key nueva sin su rama no puede terminar mandando otra orden.
    return Promise.reject(new Error(`Acción MDM sin camino de ejecución: ${String(run.key)}`));
}

/**
 * Ejecuta una acción sobre uno o varios equipos.
 *
 * Los endpoints del backoffice son por crédito, así que un lote son N llamadas. Van con
 * `allSettled`: que un equipo falle no cancela a los demás, y el resultado dice cuántos pasaron.
 */
export function useMdmDeviceAction() {
    const queryClient = useQueryClient();
    // Candado síncrono: `isPending` tarda un render en encenderse y un doble clic manda dos órdenes.
    const inFlight = useRef(false);
    // Desmontada la pantalla, los callbacks de `mutate` ya no corren: el aviso lo da el hook.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const mutation = useMutation<MdmActionOutcome, Error, MdmActionRun>({
        mutationFn: async (run) => {
            const results = await Promise.allSettled(
                run.devices.map((device) => runOne(run, device)),
            );
            const items = results.map((result) =>
                result.status === 'rejected'
                    ? { errorMessage: mdmActionErrorMessage(result.reason), leftUnprotected: false }
                    : readActionItemOutcome(result.value),
            );
            const failures = items
                .map((item) => item.errorMessage)
                .filter((message): message is string => message !== undefined);
            // Un equipo que quedó suelto NO entra en `succeeded` aunque el lambda no lo declare
            // en error: contarlo como éxito pinta un toast verde sobre un equipo sin candado.
            const accepted = items.filter(
                (item) => item.errorMessage === undefined && !item.leftUnprotected,
            );
            return {
                succeeded: accepted.length,
                failed: failures.length,
                pending: accepted.filter((item) => item.pending === true).length,
                firstErrorMessage: failures.at(0),
                unprotected: items.filter((item) => item.leftUnprotected).length,
                // El lote se programa en la misma llamada: la hora del primero los cubre.
                lockEffectiveAt: run.key === 'lock' ? accepted.at(0)?.effectiveAt : undefined,
                pin: accepted.at(0)?.pin,
                pinValiditySeconds: accepted.at(0)?.validitySeconds,
            };
        },
        onSuccess: (outcome, run) => {
            // El PIN no va a un toast: es de un solo uso y solo se muestra en su diálogo.
            if (mounted.current || outcome.pin) return;
            announceMdmOutcome(outcomeContextOf(run), outcome);
        },
        onError: (error, run) => {
            if (!mounted.current) announceMdmFailure(outcomeContextOf(run), error);
        },
        onSettled: (_outcome, _error, run) => {
            inFlight.current = false;
            void queryClient.invalidateQueries({ queryKey: MDM_FLEET_KEY });
            void queryClient.invalidateQueries({ queryKey: CREDIT_FILE_DEVICE_KEY });
            void queryClient.invalidateQueries({ queryKey: INSTALLMENTS_LIST_KEY });
            for (const device of run.devices) {
                void queryClient.invalidateQueries({
                    queryKey: [...MDM_OPERATIONS_KEY, device.creditId],
                });
            }
        },
    });
    const { mutate: rawMutate } = mutation;
    /** Ignora la orden si la anterior sigue en vuelo; así ninguna pantalla manda duplicados. */
    const mutate = useCallback<typeof rawMutate>(
        (run, options) => {
            if (inFlight.current) return;
            inFlight.current = true;
            rawMutate(run, options);
        },
        [rawMutate],
    );
    return { ...mutation, mutate };
}

/**
 * Lo que la flota tiene cacheado bajo `MDM_FLEET_KEY`: los nueve streams por `mdmStatus`, que son
 * infinitos, y la búsqueda por IMEI, que es una sola página.
 */
type MdmFleetCache = InfiniteData<LoanDeviceFleetResponse> | LoanDeviceFleetResponse;

/** Una página con la fila del crédito pisada por lo que acaba de contestar el proveedor. */
function mergeFleetPage(
    page: LoanDeviceFleetResponse,
    fresh: LoanDeviceFleetItem,
): LoanDeviceFleetResponse {
    if (!page.items.some((row) => row.creditId === fresh.creditId)) return page;
    return {
        ...page,
        // `{ ...row, ...fresh }`: lo ausente en `fresh` —marca, modelo, SKU— conserva lo que ya
        // estaba. El refresh no salta al wizard a resolverlos, y reemplazar la fila los borraría.
        items: page.items.map((row) =>
            row.creditId === fresh.creditId ? { ...row, ...fresh } : row,
        ),
    };
}

/**
 * Asienta la fila recién consultada en la caché de la flota, en vez de invalidarla: invalidar
 * refetchearía las N páginas acumuladas de cada stream.
 */
function mergeFleetDevice(queryClient: QueryClient, fresh: LoanDeviceFleetItem): void {
    queryClient.setQueriesData<MdmFleetCache>({ queryKey: MDM_FLEET_KEY }, (old) => {
        // Sin caché no hay nada que mergear; `undefined` es como TanStack dice «no escribas».
        if (!old) return undefined;
        if (!('pages' in old)) return mergeFleetPage(old, fresh);
        return { ...old, pages: old.pages.map((page) => mergeFleetPage(page, fresh)) };
    });
}

/** Lo que dejó UNA consulta: el veredicto y la fila ya mergeada con lo que contestó el proveedor. */
export interface MdmRefreshVerdict {
    review: MdmRefreshReview;
    device: MdmFleetDevice;
}

export interface MdmDeviceRefreshResult {
    /**
     * Lanza la consulta de UN equipo. Ignora la llamada si la anterior sigue en vuelo. Nunca
     * rechaza: `null` es «ignorada o falló», y el fallo se lee en `isError`/`error`.
     */
    refresh: (creditId: string) => Promise<MdmRefreshVerdict | null>;
    /** El veredicto de la última consulta que salió bien. `null` mientras no haya ninguna. */
    lastReview: MdmRefreshReview | null;
    isPending: boolean;
    isError: boolean;
    error: Error | null;
}

/**
 * La consulta al proveedor de UN equipo. Va aparte de `useMdmDeviceAction` porque devuelve un
 * resultado que la ficha pinta, no un desenlace que se anuncia y se tira.
 */
export function useMdmDeviceRefresh(): MdmDeviceRefreshResult {
    const queryClient = useQueryClient();
    const [lastReview, setLastReview] = useState<MdmRefreshReview | null>(null);
    // Candado síncrono: `isPending` tarda un render en encenderse y un doble clic consulta dos veces.
    const inFlight = useRef(false);

    const mutation = useMutation({
        mutationFn: (creditId: string) => mdmBackofficeApi.refreshMdmDevice(creditId),
        // El veredicto anterior se borra al arrancar: dejarlo en pantalla mientras se consulta de
        // nuevo le muestra al operador un resultado que ya no es el de este equipo.
        onMutate: () => {
            setLastReview(null);
        },
        onSuccess: (result) => {
            setLastReview(toRefreshReview(result.review));
            mergeFleetDevice(queryClient, result.device);
            // El bloque del expediente que lee Cobranza NO cuelga de la flota: sin invalidarlo, el
            // toast diría una cosa y el equipo de al lado seguiría mostrando la fila vieja.
            void queryClient.invalidateQueries({
                queryKey: [...CREDIT_FILE_DEVICE_KEY, result.device.creditId],
            });
            void queryClient.invalidateQueries({
                queryKey: [...MDM_OPERATIONS_KEY, result.device.creditId],
            });
        },
    });

    const { mutateAsync } = mutation;
    // Nunca rechaza: el fallo se lee en `isError`/`error`, y así ningún `onClick` deja una promesa
    // sin atajar. `null` es «ignorada o falló», no un veredicto.
    const refresh = useCallback(
        async (creditId: string): Promise<MdmRefreshVerdict | null> => {
            if (inFlight.current) return null;
            inFlight.current = true;
            try {
                const result = await mutateAsync(creditId);
                return {
                    review: toRefreshReview(result.review),
                    device: toFleetDevice(result.device),
                };
            } catch {
                return null;
            } finally {
                inFlight.current = false;
            }
        },
        [mutateAsync],
    );

    return {
        refresh,
        lastReview,
        isPending: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
    };
}
